<template>
  <Dropdown
    v-if="col.filterSettings && col.filterSettings.type === 'dropdown'"
    v-model="inputValue"
    :options="options"
    option-label="label"
    option-value="value"
    :placeholder="col.header"
    :show-clear="true"
    @change="() => { updateValue(inputValue); }"
  />
  <LanguageMultiDropdown
    v-else-if="col.filterSettings && col.filterSettings.type === 'language'"
    v-model="inputValue"
    :options="options"
    option-label="label"
    option-value="value"
    :placeholder="col.header"
    @update:model-value="updateValue(inputValue);"
  />
  <MultiSelect
    v-else-if="col.filterSettings && col.filterSettings.type === 'multiSelect'"
    v-model="inputValue"
    :options="options"
    option-label="label"
    option-value="value"
    :placeholder="col.header"
    :filter="col.filterSettings.showFilterInput ?? true"
    :show-clear="true"
    @change="() => { updateValue(inputValue); }"
  />
  <CustomDatePicker
    v-else-if="(col?.filterSettings && col?.filterSettings.type === 'date') || col?.type === 'datetime'"
    v-model="inputValue"
    :custom-min-date="5"
    :configuration="{ showLabels: false }"
    :custom-options-items="customOptionsItems"
    @update:model-value="updateValue($event);"
  />
  <CountryMultiDropdown
    v-else-if="col.filterSettings && col.filterSettings.type === 'country'"
    v-model="inputValue"
    :filter="col.filterSettings.showFilterInput?? true"
    @update:model-value="updateValue(inputValue);"
  />
  <InputText
    v-else
    v-model="inputValue"
    class="p-column-filter"
    :placeholder="col.header"
    @keydown.enter="filterCallback"
    @keyup="updateValue(inputValue)"
  />
</template>

<script lang="ts">
import {
  defineComponent, onBeforeMount,
  PropType,
  Ref,
  ref,
  watch,
} from 'vue';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import {
  IntervalDateEnum, SpmTableColumns, SpmTableFilterOption,
} from '@/types';
import { useI18n } from 'vue-i18n';
import CustomDatePicker from '@/components/fields/CustomDatePicker.vue';
import MultiSelect from 'primevue/multiselect';
import LanguageMultiDropdown from '@/components/fields/LanguageMultiDropdown.vue';
import CountryMultiDropdown from '@/components/fields/CountryMultiDropdown.vue';

export default defineComponent({
  name: 'Filter',

  components: {
    LanguageMultiDropdown,
    MultiSelect,
    CountryMultiDropdown,
    CustomDatePicker,
    InputText,
    Dropdown,
  },

  props: {
    col: {
      type: Object as PropType<SpmTableColumns>,
      required: true,
    },

    filterModel: {
      type: Object,
      required: true,
    },

    filterCallback: {
      type: Function,
      required: true,
    },

    updateValue: {
      type: Function,
      required: true,
    },

    customOptionsItems: {
      type: Array as PropType<IntervalDateEnum[]>,
      required: false,
      default: () => ([]),
    },
  },

  setup(props) {
    const { t } = useI18n();
    const inputValue = ref(props.filterModel.value);
    const options: Ref<SpmTableFilterOption[]|Function> = ref(props.col.filterSettings?.options ?? []);

    watch(props, () => { inputValue.value = props.filterModel.value; });

    onBeforeMount(async () => {
      if (typeof options.value !== 'function') {
        if (props.col.filterSettings && options.value.length && props.col.translatable) {
          (options.value ?? []).forEach((option: {value: string; label: string}) => {
            // eslint-disable-next-line no-param-reassign
            option.label = t(option.label);
          });
        }
      } else {
        options.value = await options.value();
      }
    });

    return {
      inputValue,
      options,
    };
  },

});
</script>
